import { Album, SortingConfig } from '../types/album';
import { ArtistLookupResult } from '../services/musicbrainzService';

export const SORTING_CONFIG: SortingConfig = {
  articlesToIgnore: {
    english: ['the', 'a', 'an'],
    portuguese: ['o', 'a', 'os', 'as'],
    french: ['le', 'la', 'les', 'l\'']
  }
};

export function removeArticles(text: string): string {
  const allArticles = [
    ...SORTING_CONFIG.articlesToIgnore.english,
    ...SORTING_CONFIG.articlesToIgnore.portuguese,
    ...SORTING_CONFIG.articlesToIgnore.french
  ];
  
  const words = text.toLowerCase().trim().split(/\s+/);
  if (words.length === 0) return text;
  
  const firstWord = words[0];
  if (allArticles.includes(firstWord)) {
    return words.slice(1).join(' ');
  }
  
  return text;
}

export function extractLastName(artistName: string): string {
  const cleanName = removeArticles(artistName);
  const parts = cleanName.trim().split(/\s+/);
  
  if (parts.length === 1) return parts[0];
  
  return parts[parts.length - 1];
}

// Enhanced sort key generation with MusicBrainz data
export function generateSortKeyWithMusicBrainz(
  album: Album, 
  musicbrainzResult?: ArtistLookupResult
): string {
  if (album.customSortName) {
    return `${album.customSortName.toLowerCase()}_${album.year}`;
  }
  
  if (album.isVariousArtists) {
    return `${removeArticles(album.title).toLowerCase()}_${album.year}`;
  }
  
  let sortName: string;
  
  // Use MusicBrainz sort-name if available and confident
  if (musicbrainzResult && musicbrainzResult.found && musicbrainzResult.confidence > 0.7) {
    if (musicbrainzResult.sortName) {
      // Use the expertly curated MusicBrainz sort-name
      sortName = musicbrainzResult.sortName;
    } else if (musicbrainzResult.isIndividualPerson) {
      // For individual persons without sort-name, sort by last name
      sortName = extractLastName(musicbrainzResult.artistName);
    } else {
      // For groups/bands without sort-name, sort by full name (minus articles)
      sortName = removeArticles(musicbrainzResult.artistName);
    }
  } else {
    // Fallback to conservative approach: default to band/group sorting
    // This avoids the problematic band name splitting from before
    sortName = removeArticles(album.artist);
  }
  
  return `${sortName.toLowerCase()}_${album.year}`;
}

// Legacy function for backward compatibility
export function generateSortKey(album: Album): string {
  // Use conservative approach - treat everything as bands by default
  if (album.customSortName) {
    return `${album.customSortName.toLowerCase()}_${album.year}`;
  }
  
  if (album.isVariousArtists) {
    return `${removeArticles(album.title).toLowerCase()}_${album.year}`;
  }
  
  // Default to band/group sorting (remove articles only)
  const sortName = removeArticles(album.artist);
  return `${sortName.toLowerCase()}_${album.year}`;
}

export function sortAlbums(albums: Album[]): Album[] {
  return [...albums].sort((a, b) => {
    const keyA = a.sortKey || generateSortKey(a);
    const keyB = b.sortKey || generateSortKey(b);
    return keyA.localeCompare(keyB);
  });
}